import { CartVehicle, OrderFormData } from 'src/store/models/ShoppingCartModels'
import { getCartStorageKey } from '../store/helpers'
import { CART_VERSION_STRING, MY_PLACE_SELLER } from 'common'
import {
  CartProduct,
  PartsLocations,
  SellerCartData,
  SellerCartVehicle,
  SellerWithLocations,
} from '../store/ISellerCart'
import { DeliveryDetail } from 'src/api/cart/interfaces'
import { defaultCart } from '../store/cartStore'
import { cloneDeep } from 'lodash-es'
import { IS_INTEGRATED_VERSION } from 'src/config/ConfigManager'
import { Vehicle } from 'src/store/models/Vehicles'

export interface ShoppingCartData {
  version: string
  vehicle?: Vehicle
  vehicles?: Array<CartVehicle>
}

const compareVersions = (v1: string, v2: string): number => {
  const p1 = v1.split('.').map(Number)
  const p2 = v2.split('.').map(Number)

  for (let i = 0; i < Math.max(p1.length, p2.length); i++) {
    const n1 = p1[i] ?? 0
    const n2 = p2[i] ?? 0
    if (n1 > n2) return 1
    if (n1 < n2) return -1
  }
  return 0
}

const migrateCartIfNeeded = (
  parsedData: ShoppingCartData,
  userId: string
): void => {
  // Already on latest version
  if (compareVersions(parsedData.version, CART_VERSION_STRING) >= 0) return
  if (!parsedData.vehicles?.length) {
    localStorage.removeItem(getCartStorageKey(userId))
    return
  }

  const sellerCartData: SellerCartData = cloneDeep(defaultCart)

  for (const oldVehicle of parsedData.vehicles) {
    const locationMap = new Map<number, PartsLocations>()

    for (const product of oldVehicle.products ?? []) {
      if (!product.orderSelections?.length) continue
      if (!product.location?.length) continue

      for (const selection of product.orderSelections) {
        const location = product.location.find(
          (loc) => loc.locationId === selection.locationId
        )
        if (!location) continue

        // Initialize location bucket if missing
        if (!locationMap.has(Number(location.locationId))) {
          locationMap.set(Number(location.locationId), {
            ...location,
            parts: [],
            deliveryDetail: {} as DeliveryDetail,
          })
        }

        const cartProduct: CartProduct = {
          ...product,
          quantityRequested: selection.quantityRequested ?? 1,
          errors: product.errors ?? [],
          bundleInfo: [],
        }

        locationMap.get(Number(location.locationId))?.parts.push(cartProduct)
      }
    }

    if (!locationMap.size) continue

    const sellerWithLocations: SellerWithLocations = {
      ...MY_PLACE_SELLER,
      locations: Array.from(locationMap.values()),
      noteToStore: oldVehicle.orderFormData?.noteToStore ?? '',
      poNumber: oldVehicle.orderFormData?.poNumber ?? '',
    }

    const sellerCartVehicle: SellerCartVehicle = {
      vehicle: oldVehicle.vehicle,
      sellers: [sellerWithLocations],
      orderFormData: oldVehicle.orderFormData ?? ({} as OrderFormData),
      specifications: oldVehicle.specifications ?? [],
      laborResults: oldVehicle.laborResults ?? [],
      quoteSourceId: oldVehicle.quoteSourceId,
    }

    sellerCartData.vehicles.push(sellerCartVehicle)
  }

  // If nothing was migrated, reset cart
  if (!sellerCartData.vehicles.length) {
    localStorage.removeItem(getCartStorageKey(userId))
    return
  }

  // Persist migrated cart
  localStorage.setItem(
    getCartStorageKey(userId),
    JSON.stringify(sellerCartData)
  )
}

export const initCartMigration = (userId: string): void => {
  if (!userId) return
  const storageKey = getCartStorageKey(userId)
  if (IS_INTEGRATED_VERSION) {
    localStorage.removeItem(storageKey)
  }

  const raw = localStorage.getItem(storageKey)

  try {
    if (raw) {
      const parsed = JSON.parse(raw)
      migrateCartIfNeeded(parsed, userId)
    }
  } catch {
    localStorage.removeItem(storageKey)
  }
}
